import { Fragment, useMemo } from 'react';
import { useRouter } from 'next/router';
import Icon from '@ant-design/icons';
import { Col, Row } from 'antd';

import Image from 'global/Image';
import CornerUpRightSvg from 'global/Icons/CornerUpRightSvg';
import ChevronDownSvg from 'global/Securities/components/Icons/ChevronDownSvg';
import VerifiedSvg from 'global/Icons/VerifiedSvg';
import { ShareTradeInfo, ShareTradeLabel } from 'typings/api/shareTradeChat';
import { ShareOrderStatus } from 'typings/api/shareTradeShare';
import {
  trackChatCompanySymbol,
  trackChatPreviousOrder,
} from 'features/sharetrade/analytics/shareTradeGroupChat';
import { ChatRoomType } from 'features/chat/types';
import If from 'global/Common/If';
import {
  ContainerRow,
  ContentButton,
  ContentRow,
  PreviousOrderButton,
  PreviousOrderCol,
  PriceText,
  shareTradeBubbleConfig,
  TimeText,
  Title,
  TradeRow,
  TitleRow,
  BadgeWrapper,
  Username,
  TotalInvestmentText,
} from './ShareTradeAttachment.styled';

interface ShareTradeAttachmentProps {
  roomId?: number;
  messageId?: number;
  chatRoomType?: ChatRoomType;
  info?: ShareTradeInfo | Record<string, never>;
  username?: string;
  usernameColor?: string;
  previousButtonText?: string;
  isVerified?: boolean;
  hideUsername?: boolean;
  hidePreviousOrder?: boolean;
  onPreviousOrderClick?(): void;
  isPreview?: boolean;
}

const ShareTradeAttachment = ({
  roomId,
  messageId,
  chatRoomType,
  info = {},
  username = '',
  usernameColor,
  previousButtonText = 'See Previous Order',
  isVerified = false,
  hideUsername = false,
  hidePreviousOrder = false,
  onPreviousOrderClick = () => { },
  isPreview = false,
}: ShareTradeAttachmentProps) => {
  const router = useRouter();

  const {
    symbol,
    text,
    price,
    status,
    updated_price: updatedPrice,
    order_type: orderType,
    order_time: orderTime,
    total_investment: totalInvestment,
    label,
  } = info;

  const isDayTrade = label === ShareTradeLabel.DAY_TRADE;
  const isSplitOrder = label === ShareTradeLabel.SPLIT_ORDER;

  const isLineThrough = useMemo(
    () => [ShareOrderStatus.Amend, ShareOrderStatus.Cancel].includes(status),
    [status],
  );

  const getOrderTitleBadge = () => {
    if (!label || label === ShareTradeLabel.UNSPECIFIED) return null;

    return (
      <BadgeWrapper>
        <If condition={isSplitOrder}>
          <Image src="/images/share-trade/split-order.svg" />
        </If>

        <If condition={isDayTrade}>
          <Image src="/images/share-trade/day-trade.svg" />
        </If>
      </BadgeWrapper>
    );
  };

  const handlePreviousOrderClick = () => {
    if (isPreview) return;

    trackChatPreviousOrder(
      roomId?.toString(),
      messageId?.toString(),
      symbol,
      status,
      chatRoomType,
    );
    onPreviousOrderClick();
  };

  const handleContentClick = () => {
    if (isPreview) return;

    trackChatCompanySymbol(
      roomId?.toString(),
      messageId?.toString(),
      symbol,
      status,
      chatRoomType,
    );
    router.push(`/symbol/${symbol}`);
  };

  return (
    <ContainerRow
      align="middle"
      justify="space-between"
      orderType={orderType}
      className="share-preview"
    >
      {hidePreviousOrder && (
        <PreviousOrderCol className="st-col-header" span={24}>
          <PreviousOrderButton
            type="link"
            icon={<Icon component={CornerUpRightSvg} />}
            onClick={handlePreviousOrderClick}
          >
            {previousButtonText}
          </PreviousOrderButton>
        </PreviousOrderCol>
      )}
      <Col span={24}>
        <ContentButton onClick={handleContentClick}>
          <ContentRow align="middle" gutter={[0, 6]}>
            {!hideUsername && (
              <Col span={24}>
                <Row align="middle" gutter={[4, 0]}>
                  <Col>
                    <Username color={usernameColor}>{username}</Username>
                  </Col>
                  {isVerified && <Icon component={VerifiedSvg} />}
                </Row>
              </Col>
            )}
            <Col span={24}>
              <Row align="middle" justify="space-between">
                <Col span={22}>
                  <TradeRow>
                    <Col span={24}>
                      <TitleRow>
                        <Title>
                          {text.split(symbol).map((item) => (
                            <Fragment key={item}>
                              {item || <b>{symbol}</b>}
                            </Fragment>
                          ))}
                        </Title>
                        {getOrderTitleBadge()}
                      </TitleRow>
                    </Col>
                    <PriceText lineThrough={isLineThrough}>{price}</PriceText>
                    {updatedPrice && (
                      <PriceText marginLeft="8px">{updatedPrice}</PriceText>
                    )}

                    {!!totalInvestment && (
                      <TotalInvestmentText marginLeft="4px">
                        {'• '}
                        {totalInvestment}
                      </TotalInvestmentText>
                    )}
                  </TradeRow>

                  <TimeText>
                    {new Date(orderTime).toLocaleTimeString(undefined, {
                      hour12: false,
                    })}
                  </TimeText>
                </Col>
                <Col>
                  <Icon
                    component={ChevronDownSvg}
                    style={{
                      fontSize: 16,
                      color: shareTradeBubbleConfig[orderType]?.text,
                      transform: 'rotate(-90deg)',
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </ContentRow>
        </ContentButton>
      </Col>
    </ContainerRow>
  );
};

export default ShareTradeAttachment;
